import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  VisuallyHidden,
  chakra,
  useColorModeValue,
  type BoxProps,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { MARK_PATH } from './Logo';
import { useT } from '../../i18n';
import type { LoadingState } from '../../hooks/useLoadingState';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { useFestivalTouch } from '../../hooks/useFestivalTouch';
import { LOADER_FLAME, loaderFlameOn } from '../../lib/festivalMode';

// The loading indicator (M66, D247): the Twinden mark drawn as an outline,
// with a short bright dash travelling around its perimeter and the window
// light pulsing in step. Pages, panels and sheets show it instead of a
// spinner while they wait for the server. The numbers here are the contract
// the native apps match (docs/loading-states.md §5).
export const LOADER = {
  /**
   * Length of MARK_PATH in viewBox units: two 48.4 roof slopes, two 32 walls,
   * two quarter arcs of radius 8 (12.57 each), the 34 floor, the 20 + 12
   * tail and the 10 ledge = 261.96. Set as `pathLength` on the paths, so the
   * dash and its offset are exact on every renderer.
   */
  pathLength: 262,
  /** How much of the perimeter the bright dash covers. */
  dashFraction: 0.22,
  /** One trip around the mark; the window light pulses at the same pace. */
  periodMs: 1400,
  /** The outline under the dash, as an opacity of the mark colour. */
  outlineOpacity: 0.35,
  /** Reduced motion: the whole stroke, still, at this opacity. */
  staticStrokeOpacity: 0.6,
  /** The window light's pulse: from, to and back. */
  windowOpacity: { low: 0.4, high: 1 },
  strokeWidth: 4,
  sizes: { page: 56, inline: 22 },
  /** The mark's colour on light and dark backgrounds (as the logo). */
  colors: { light: '#7A1F2B', dark: '#B27D84', window: '#F0B63F' },
} as const;

/**
 * The room a LoadingPanel takes in each size, held from the first moment
 * (before the mark is due) so nothing shifts when it appears (D248).
 */
export const PANEL_HEIGHT = { page: '168px', inline: '54px' } as const;

const DASH = LOADER.pathLength * LOADER.dashFraction;

const travel = keyframes({
  from: { strokeDashoffset: LOADER.pathLength },
  to: { strokeDashoffset: 0 },
});

const pulse = keyframes({
  '0%, 100%': { opacity: LOADER.windowOpacity.low },
  '50%': { opacity: LOADER.windowOpacity.high },
});

const Path = chakra('path');
const Circle = chakra('circle');
const G = chakra('g');

export type LoaderSize = keyof typeof LOADER.sizes;

/**
 * A live region's text, set after mount: screen readers announce a change
 * to a region's content, not a region that arrives already filled (D251).
 */
function useAnnounced(text: string): string {
  const [spoken, setSpoken] = useState('');
  useEffect(() => {
    setSpoken(text);
  }, [text]);
  return spoken;
}

interface TwindenLoaderProps extends Omit<BoxProps, 'size'> {
  /** `page`: 56px, centred, with the label beneath; `inline`: 22px. */
  size?: LoaderSize;
  /** What is loading ("Loading chats…"); the default is "Loading…". */
  label?: string;
  /**
   * Whether this mark is its own status region (announced once). Off when
   * a LoadingPanel around it owns the announcement, or when the mark is a
   * quiet extra (D250).
   */
  announce?: boolean;
}

export function TwindenLoader({
  size = 'page',
  label,
  announce = true,
  ...rest
}: TwindenLoaderProps) {
  const t = useT();
  const reduced = usePrefersReducedMotion();
  // On the festivals of lamps the window light is a diya flame (D311).
  const flame = loaderFlameOn(useFestivalTouch());
  const mark = useColorModeValue(LOADER.colors.light, LOADER.colors.dark);
  const px = LOADER.sizes[size];
  const text = label ?? t('common.loading');
  const spoken = useAnnounced(text);
  const period = `${LOADER.periodMs}ms`;
  // The label is read from the region's content: visible under the page
  // mark, hidden but present beside the inline one (D251).
  const shown = announce ? spoken : text;

  return (
    <VStack
      spacing={3}
      role={announce ? 'status' : undefined}
      aria-live={announce ? 'polite' : undefined}
      data-testid="twinden-loader"
      data-size={size}
      data-motion={reduced ? 'reduced' : 'animated'}
      display={size === 'inline' ? 'inline-flex' : 'flex'}
      {...rest}
    >
      <Box as="span" display="inline-flex" w={`${px}px`} h={`${px}px`}>
        <svg width={px} height={px} viewBox="0 0 96 96" aria-hidden="true">
          <path
            d={MARK_PATH}
            pathLength={LOADER.pathLength}
            fill="none"
            stroke={mark}
            strokeOpacity={LOADER.outlineOpacity}
            strokeWidth={LOADER.strokeWidth}
            strokeLinejoin="round"
          />
          {reduced ? (
            <path
              d={MARK_PATH}
              pathLength={LOADER.pathLength}
              fill="none"
              stroke={mark}
              strokeOpacity={LOADER.staticStrokeOpacity}
              strokeWidth={LOADER.strokeWidth}
              strokeLinejoin="round"
            />
          ) : (
            <Path
              d={MARK_PATH}
              pathLength={LOADER.pathLength}
              fill="none"
              stroke={mark}
              strokeWidth={LOADER.strokeWidth}
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray={`${DASH} ${LOADER.pathLength - DASH}`}
              strokeDashoffset={LOADER.pathLength}
              animation={`${travel} ${period} linear infinite`}
            />
          )}
          {flame ? (
            <G
              data-testid="loader-flame"
              animation={
                reduced ? undefined : `${pulse} ${period} ease-in-out infinite`
              }
            >
              <path d={LOADER_FLAME.outer.d} fill={LOADER_FLAME.outer.fill} />
              <path d={LOADER_FLAME.inner.d} fill={LOADER_FLAME.inner.fill} />
            </G>
          ) : (
            <Circle
              cx={48}
              cy={52}
              r={9}
              fill={LOADER.colors.window}
              animation={
                reduced ? undefined : `${pulse} ${period} ease-in-out infinite`
              }
            />
          )}
        </svg>
      </Box>
      {size === 'page' ? (
        <Text fontSize="14px" color="muted" textAlign="center" minH="21px">
          {shown}
        </Text>
      ) : (
        announce && <VisuallyHidden>{shown}</VisuallyHidden>
      )}
    </VStack>
  );
}

interface LoadingPanelProps extends BoxProps {
  /** From useLoadingState: when to show the mark, the slow line, Try again. */
  state: LoadingState;
  /** What is loading; the label under the mark and the status text. */
  label?: string;
  /** `inline` for sheets and small areas (D250). */
  size?: LoaderSize;
}

// A view's loading area (D248–D249): nothing for the first 150 ms, then the
// mark; after 8 s a quiet line; after a failure (or offline) Try again. One
// status region for the whole wait, whose text changes with each state, so
// each is announced once (D251); no aria-busy on it, which would hold those
// announcements back; the same height throughout, so nothing shifts when
// the mark appears.
export function LoadingPanel({
  state,
  label,
  size = 'page',
  ...rest
}: LoadingPanelProps) {
  const t = useT();
  const inline = size === 'inline';
  const text = label ?? t('common.loading');
  const failedText = state.offline
    ? t('common.loading.offline')
    : t('common.loading.failed');
  const message = state.failed
    ? failedText
    : state.loader
      ? state.slow
        ? `${text} ${t('common.loading.slow')}`
        : text
      : '';
  if (!state.show) return null;

  return (
    <VStack
      spacing={3}
      minH={PANEL_HEIGHT[size]}
      px={4}
      justify="center"
      role="status"
      aria-live="polite"
      data-testid={state.failed ? 'loading-failed' : 'loading-panel'}
      {...rest}
    >
      {state.failed ? (
        <>
          <Text fontSize="15px" color="muted" textAlign="center">
            {failedText}
          </Text>
          {state.retry && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              onClick={state.retry}
            >
              {t('common.loading.tryAgain')}
            </Button>
          )}
        </>
      ) : state.loader ? (
        <>
          <TwindenLoader size={size} label={text} announce={false} />
          {state.slow && (
            <Text
              fontSize="14px"
              color="muted"
              textAlign="center"
              data-testid="loading-slow"
            >
              {t('common.loading.slow')}
            </Text>
          )}
        </>
      ) : null}
      {/* The page mark's label and the failed line are visible text in the
          region already; the inline mark has none, so say it here. */}
      {inline && !state.failed && <VisuallyHidden>{message}</VisuallyHidden>}
    </VStack>
  );
}
